import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Calendar, Clock, Users, Tablet, CalendarDays, CheckCircle2, Circle, ArrowRight, Sparkles, Rocket } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { config } from '../../lib/config';
import { useAuthStore } from '../../stores/auth.store';
import { Button } from '../ui/button';
const PEOPLE_API_URL = config.api.people;
const TIME_API_URL = config.api.time;
const LEAVE_API_URL = config.api.leave;
/**
 * Getting Started Dashboard for new accounts
 * Shows onboarding steps to help users set up their workspace
 */
export function GettingStarted() {
    const { tokens, user } = useAuthStore();
    const [status, setStatus] = useState({
        hasPayPeriod: false,
        hasLeavePolicy: false,
        hasEmployees: false,
        hasSchedule: false,
        hasKiosk: false,
        isLoading: true,
    });
    useEffect(() => {
        const checkSetupStatus = async () => {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokens?.idToken}`,
                'X-Tenant-Id': user?.tenantId || '',
            };
            try {
                // Check for employees (admin is auto-created, so >1 means they've added others)
                const employeesRes = await fetch(`${PEOPLE_API_URL}/employees`, { headers });
                let hasEmployees = false;
                if (employeesRes.ok) {
                    const employeesData = await employeesRes.json();
                    // More than 1 employee means they've added someone beyond the auto-created admin
                    hasEmployees = (employeesData?.employees?.length || 0) > 1;
                }
                // Check for pay periods (404 means none exist)
                const payPeriodsRes = await fetch(`${TIME_API_URL}/config/pay-periods`, { headers });
                let hasPayPeriod = false;
                if (payPeriodsRes.ok) {
                    const payPeriodsData = await payPeriodsRes.json();
                    hasPayPeriod = (payPeriodsData?.configs?.length || 0) > 0;
                }
                // Check for leave policies
                const leavePoliciesRes = await fetch(`${LEAVE_API_URL}/policies`, { headers });
                let hasLeavePolicy = false;
                if (leavePoliciesRes.ok) {
                    const leavePoliciesData = await leavePoliciesRes.json();
                    hasLeavePolicy = (leavePoliciesData?.policies?.length || 0) > 0;
                }
                // Check for kiosk devices
                const kioskRes = await fetch(`${TIME_API_URL}/kiosk/devices`, { headers });
                let hasKiosk = false;
                if (kioskRes.ok) {
                    const kioskData = await kioskRes.json();
                    hasKiosk = (kioskData?.devices?.length || 0) > 0;
                }
                // Check for schedules (we'll assume false for now as schedule feature is optional)
                const hasSchedule = false;
                setStatus({
                    hasPayPeriod,
                    hasLeavePolicy,
                    hasEmployees,
                    hasSchedule,
                    hasKiosk,
                    isLoading: false,
                });
            }
            catch (error) {
                console.error('Error checking setup status:', error);
                setStatus(prev => ({ ...prev, isLoading: false }));
            }
        };
        if (tokens?.idToken && user?.tenantId) {
            void checkSetupStatus();
        }
    }, [tokens, user]);
    const steps = [
        {
            id: 'pay-period',
            title: 'Create your first pay period',
            description: 'Set up pay period schedules to track employee hours and run payroll.',
            icon: _jsx(Calendar, { className: "w-6 h-6" }),
            link: '/settings/pay-periods',
            linkText: 'Set Up Pay Periods',
            completed: status.hasPayPeriod,
        },
        {
            id: 'leave-policy',
            title: 'Create a time off policy',
            description: 'Define PTO, sick leave, and other time off policies for your team.',
            icon: _jsx(CalendarDays, { className: "w-6 h-6" }),
            link: '/leave/policies',
            linkText: 'Create Policy',
            completed: status.hasLeavePolicy,
        },
        {
            id: 'employee',
            title: 'Add your first employee',
            description: 'Add team members so they can clock in, request time off, and more.',
            icon: _jsx(Users, { className: "w-6 h-6" }),
            link: '/people/new',
            linkText: 'Add Employee',
            completed: status.hasEmployees,
        },
        {
            id: 'schedule',
            title: 'Create a schedule',
            description: 'Set up work schedules and shifts for your team.',
            icon: _jsx(Clock, { className: "w-6 h-6" }),
            link: '/schedule',
            linkText: 'Create Schedule',
            completed: status.hasSchedule,
            optional: true,
        },
        {
            id: 'kiosk',
            title: 'Set up a time clock kiosk',
            description: 'Create a shared device for employees to clock in and out.',
            icon: _jsx(Tablet, { className: "w-6 h-6" }),
            link: '/settings/time-clock-devices',
            linkText: 'Set Up Kiosk',
            completed: status.hasKiosk,
            optional: true,
        },
    ];
    const completedCount = steps.filter(s => s.completed).length;
    const requiredSteps = steps.filter(s => !s.optional);
    const requiredCompleted = requiredSteps.filter(s => s.completed).length;
    const allRequiredComplete = requiredCompleted === requiredSteps.length;
    if (status.isLoading) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-[400px]", children: _jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" }) }));
    }
    return (_jsxs("div", { className: "space-y-8", children: [_jsxs("div", { className: "text-center", children: [_jsx("div", { className: "inline-flex items-center justify-center w-16 h-16 bg-brand-600 rounded-2xl mb-4", children: _jsx(Rocket, { className: "w-8 h-8 text-white" }) }), _jsx("h1", { className: "text-3xl font-bold text-gray-900 mb-2", children: "Welcome to Lalo HR! \uD83C\uDF89" }), _jsx("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: "Let's get your workspace set up. Complete these steps to start tracking time, managing employees, and running payroll." })] }), _jsxs("div", { className: "bg-white rounded-xl border border-gray-200 p-6 shadow-sm", children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Sparkles, { className: "w-5 h-5 text-yellow-500" }), _jsx("span", { className: "font-semibold text-gray-900", children: "Setup Progress" })] }), _jsxs("span", { className: "text-sm text-gray-600", children: [completedCount, " of ", steps.length, " completed"] })] }), _jsx("div", { className: "w-full bg-gray-200 rounded-full h-3", children: _jsx("div", { className: "bg-brand-600 h-3 rounded-full transition-all duration-500", style: { width: `${(completedCount / steps.length) * 100}%` } }) }), allRequiredComplete && (_jsxs("p", { className: "text-sm text-green-600 mt-3 flex items-center gap-1", children: [_jsx(CheckCircle2, { className: "w-4 h-4" }), "All required steps completed! You're ready to go."] }))] }), _jsx("div", { className: "space-y-4", children: steps.map((step, index) => (_jsx("div", { className: `bg-white rounded-xl border ${step.completed
                        ? 'border-green-200 bg-green-50/50'
                        : 'border-gray-200'} p-6 shadow-sm transition-all hover:shadow-md`, children: _jsxs("div", { className: "flex items-start gap-4", children: [_jsx("div", { className: `flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${step.completed
                                    ? 'bg-green-100 text-green-600'
                                    : 'bg-gray-100 text-gray-400'}`, children: step.completed ? (_jsx(CheckCircle2, { className: "w-6 h-6" })) : (_jsx("span", { className: "text-lg font-semibold", children: index + 1 })) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center gap-2 mb-1", children: [_jsx("h3", { className: `text-lg font-semibold ${step.completed ? 'text-green-900' : 'text-gray-900'}`, children: step.title }), step.optional && (_jsx("span", { className: "px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-100 rounded", children: "Optional" }))] }), _jsx("p", { className: `text-sm mb-4 ${step.completed ? 'text-green-700' : 'text-gray-600'}`, children: step.description }), !step.completed && (_jsx(Link, { to: step.link, children: _jsxs(Button, { className: "gap-2", children: [_jsxs("span", { className: "flex items-center gap-2", children: [step.icon, step.linkText] }), _jsx(ArrowRight, { className: "w-4 h-4" })] }) })), step.completed && (_jsx(Link, { to: step.link, children: _jsx(Button, { variant: "outline", size: "sm", className: "text-green-700 border-green-300 hover:bg-green-50", children: "View Settings" }) }))] }), _jsx("div", { className: `flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${step.completed
                                    ? 'bg-green-100 text-green-600'
                                    : 'bg-brand-50 text-brand-600'}`, children: step.icon })] }) }, step.id))) }), _jsx("div", { className: "bg-brand-50 rounded-xl border border-brand-100 p-6", children: _jsxs("div", { className: "flex items-start gap-4", children: [_jsx("div", { className: "flex-shrink-0 w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center", children: _jsx(Circle, { className: "w-5 h-5 text-brand-600" }) }), _jsxs("div", { children: [_jsx("h3", { className: "font-semibold text-gray-900 mb-1", children: "Need help getting started?" }), _jsx("p", { className: "text-sm text-gray-600 mb-3", children: "Check out our documentation or reach out to support if you have any questions." }), _jsxs("div", { className: "flex gap-3", children: [_jsx("a", { href: "https://lalohr.com/docs", target: "_blank", rel: "noopener noreferrer", children: _jsx(Button, { variant: "outline", size: "sm", children: "View Documentation" }) }), _jsx("a", { href: "mailto:support@lalohr.com", children: _jsx(Button, { variant: "ghost", size: "sm", children: "Contact Support" }) })] })] })] }) })] }));
}
/**
 * Hook to check if account needs onboarding
 * Returns true if this is a new account with no setup completed
 */
export function useNeedsOnboarding() {
    const { tokens, user } = useAuthStore();
    const [state, setState] = useState({ needsOnboarding: false, isLoading: true });
    useEffect(() => {
        const checkOnboarding = async () => {
            if (!tokens?.idToken || !user?.tenantId) {
                setState({ needsOnboarding: false, isLoading: false });
                return;
            }
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokens.idToken}`,
                'X-Tenant-Id': user.tenantId,
            };
            try {
                // Check employees - during signup, admin gets auto-created as an employee
                // So a "new" account has exactly 1 employee (the admin)
                const employeesRes = await fetch(`${PEOPLE_API_URL}/employees`, { headers });
                let employeeCount = 0;
                if (employeesRes.ok) {
                    const employeesData = await employeesRes.json();
                    employeeCount = employeesData?.employees?.length || 0;
                }
                // Check pay periods
                const payPeriodsRes = await fetch(`${TIME_API_URL}/config/pay-periods`, { headers });
                let hasPayPeriods = false;
                if (payPeriodsRes.ok) {
                    const payPeriodsData = await payPeriodsRes.json();
                    hasPayPeriods = payPeriodsData?.configs?.length > 0;
                }
                // Show onboarding if:
                // - Only 1 employee (the auto-created admin) AND no pay periods
                // Once they add another employee OR create a pay period, show regular dashboard
                const needsOnboarding = employeeCount <= 1 && !hasPayPeriods;
                setState({ needsOnboarding, isLoading: false });
            }
            catch (error) {
                console.error('Error checking onboarding status:', error);
                // On error, default to showing onboarding for new accounts
                setState({ needsOnboarding: true, isLoading: false });
            }
        };
        void checkOnboarding();
    }, [tokens, user]);
    return state;
}
